import { FC, useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { APPLIA_WEBSITE_URL } from "../../config/env";

import { TERMS_AND_AGREEMENT_MODAL_COOKIE_NAME } from "./TermsAndAgreementModal.const";


const TermsAndAgreementModal: FC = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isChecked, setIsChecked] = useState(false);

	// Prevent website scroll when modal is open
	useLockBodyScroll(isOpen);

	const acceptTerms = useCallback(() => {
		setIsOpen(false);
		Cookies.set(TERMS_AND_AGREEMENT_MODAL_COOKIE_NAME, "true", { expires: 365 });
	}, []);

	const handleSubmit = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			if (isChecked) {
				acceptTerms();
			}
		},
		[acceptTerms, isChecked],
	);

	// Check on mount if the user has agreed to the terms and conditions yet
	useEffect(() => {
		const isModalAccepted = Cookies.get(TERMS_AND_AGREEMENT_MODAL_COOKIE_NAME);

		if (!isModalAccepted) {
			setIsOpen(true);
		}
	}, []);

	// Return noting if the user has agreed to the terms and conditions
	if (!isOpen) {
		return null;
	}

	return (
		<div className="fixed inset-0 flex items-start md:items-center justify-center z-50 p-4 overflow-auto">
			{/* Modal backdrop overlay */}
			<div className="fixed inset-0 bg-gray-900 opacity-50" />

			{/* Modal content */}
			<section className="bg-white p-6 max-w-md mx-auto rounded shadow-lg z-10 max-h-full overflow-y-auto overflow-x-hidden">
				{/* Modal header */}
				<header className="flex justify-between items-center mb-4">
					<h2 className="h2 text-secondary">Proprietary information notice</h2>
				</header>

				{/* Modal body */}
				<p className="mb-3 text-sm">The content of this APPLiA report is proprietary information of APPLiA and its licensors and is protected by intellectual property rights including copyright and database rights.</p>
				<p className="mb-3 text-sm">{"This document may only be used for consultation by the recipient of this report subject to prior acknowledgment and acceptance of the terms of this notice, and solely for the recipient's own internal purposes - expressly excluding any commercial use of the information contained herein. Any reproduction in part or whole of this APPLiA report and any extraction of the information contained herein are subject to prior written authorization from APPLiA. Any use of this APPLiA report in part or whole other than in accordance with the above limited permission may trigger enforcement by APPLiA and/or its licensors."}</p>
				<p className="mb-6 text-sm">{"We care about your data, and we'd use cookies only to improve your experience. By using this website, you accept our Cookies Policy."}</p>

				{/* Terms and Conditions form */}
				<form onSubmit={handleSubmit}>
					<label className="flex items-start justify-start space-x-2 mb-6">
						<input type="checkbox" className="form-checkbox mt-1" required onChange={(e) => setIsChecked(e.target.checked)} />
						<span className="text-gray-800 text-sm font-semibold">
							By clicking &quot;Accept&quot;, you agree to our{" "}
							<a
								className="text-primary hover:text-primary-hover underline"
								href="/files/pdf/applia-gdpr-website-2025.pdf"
								target="_blank"
								rel="noopener noreferrer"
							>
								Terms & Conditions
							</a>
							,{" "}
							<a
								className="text-primary hover:text-primary-hover underline"
								href="/files/pdf/applia-data-protection-policy-2025.pdf"
								target="_blank"
								rel="noopener noreferrer"
							>
								Privacy Policy
							</a>
							{" "}and our Disclaimer.
						</span>
					</label>

					<div className="flex items-center gap-4">
						<button
							disabled={!isChecked}
							type="submit"
							className="text-white bg-primary hover:bg-primary-hover focus:ring-4 focus:ring-blue-300 font-medium rounded-xl text-sm text-center px-10 py-2.5 focus:outline-none disabled:bg-gray-300 transition-colors"
						>
							Accept
						</button>
						<a href={APPLIA_WEBSITE_URL} className="text-sm text-primary hover:text-primary-hover">
							{"Don't accept"}
						</a>
					</div>
				</form>
			</section>
		</div>
	);
};

export { TermsAndAgreementModal };
